import { useEffect, useRef, useState } from "react";
import { Camera, CheckCircle2, Images, Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { errMsg } from "@/lib/api";
import { uploadProof } from "@/lib/member";

const TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX = 5 * 1024 * 1024;
const MAX_SIDE = 2000;

const toJpeg = async (file) => {
  const bmp = await createImageBitmap(file);
  const s = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
  const c = document.createElement("canvas");
  c.width = Math.round(bmp.width * s);
  c.height = Math.round(bmp.height * s);
  c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
  const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.85));
  return new File([blob], "bukti.jpg", { type: "image/jpeg" });
};

const normalize = async (file) => {
  if (TYPES.includes(file.type) && file.size <= MAX) return file;
  try {
    return await toJpeg(file);
  } catch {
    return null;
  }
};

export const ProofUpload = ({ kind, onChange, dark = true, testid = "proof-upload" }) => {
  const ref = useRef(null);
  const camRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const pick = async (e) => {
    const raw = e.target.files?.[0];
    e.target.value = "";
    if (!raw) return;
    if (raw.type && !raw.type.startsWith("image/")) return setError("File harus berupa gambar (foto / screenshot)");
    const file = await normalize(raw);
    if (!file) return setError("Format foto tidak didukung, gunakan JPG / PNG / screenshot");
    if (file.size > MAX) return setError("Ukuran maksimal 5MB");
    setError("");
    setPreview(URL.createObjectURL(file));
    setStatus("uploading");
    onChange(null);
    try {
      onChange(await uploadProof(kind, file));
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError((err?.response?.status || 0) >= 500 || !err?.response ? "Upload bukti gagal, silakan coba lagi" : errMsg(err, "Upload gagal, coba lagi"));
    }
  };

  const box = dark ? "border-[#6b5a2a] bg-black/60 text-white" : "border-slate-300 bg-slate-50 text-slate-800";
  const btn = dark ? "border-[#6b5a2a] bg-black/40 text-[#e5b84a] hover:bg-black/70" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100";
  return (
    <div data-testid={testid}>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testid}-camera-input`} />
      <button type="button" onClick={() => ref.current?.click()} className={`flex w-full items-center gap-3 rounded-xl border-2 border-dashed p-3 text-left ${box}`} data-testid={`${testid}-btn`}>
        {preview ? (
          <img src={preview} alt="Bukti transfer" className="h-16 w-16 rounded-lg object-cover" />
        ) : (
          <span className="grid h-16 w-16 place-items-center rounded-lg bg-[#e5b84a] text-black"><Images size={26} /></span>
        )}
        <span className="flex-1">
          <span className="block text-sm font-extrabold">{preview ? "Ganti Bukti Transfer" : "Upload Bukti Transfer"}</span>
          <span className="block text-[11px] font-bold opacity-70">Pilih foto / screenshot dari galeri, maks 5MB</span>
          <span className="mt-1 flex items-center gap-1 text-[11px] font-bold" data-testid={`${testid}-status`}>
            {status === "uploading" ? <><Loader2 size={12} className="animate-spin" /> Mengunggah…</> : null}
            {status === "done" ? <span className="flex items-center gap-1 text-emerald-500"><CheckCircle2 size={12} /> Bukti tersimpan</span> : null}
            {status === "error" ? <span className="flex items-center gap-1 text-rose-500"><RefreshCw size={12} /> Gagal — ketuk untuk ulangi</span> : null}
          </span>
        </span>
      </button>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button type="button" onClick={() => ref.current?.click()} className={`inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-extrabold transition-colors ${btn}`} data-testid={`${testid}-gallery-btn`}>
          <Images size={14} /> Pilih dari Galeri
        </button>
        <button type="button" onClick={() => camRef.current?.click()} className={`inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-extrabold transition-colors ${btn}`} data-testid={`${testid}-camera-btn`}>
          <Camera size={14} /> Ambil Foto
        </button>
      </div>
      {error ? <p className="mt-1 flex items-center gap-1 text-xs font-bold text-rose-500"><AlertCircle size={12} /> {error}</p> : null}
    </div>
  );
};
